// Puter-backed sync for the vault.
//
// Remote layout (in the user's Puter home):
//   vault.enc            — encrypted vault blob (PWVT format, same as export)
//   vault.meta.json      — { modifiedAt, hash, size, updatedFrom }
//
// The meta file is unencrypted by design: it only carries non-sensitive
// metadata (a timestamp and a hash of the ciphertext) so we can detect
// changes without pulling the whole blob.
//
// Sync algorithm (run on demand, on debounced changes, or on unlock):
//   localMod = vault.vaultModifiedAt()
//   { at: lastSyncedAt, hash: lastSyncedHash } = vault.getLastSynced()
//   remote = readRemoteMeta()  // null if not yet uploaded
//
//   localChangedSinceSync = localMod > lastSyncedAt
//   remoteChangedSinceSync = remote && remote.hash !== lastSyncedHash
//
//   if !remote                     → push      (first sync)
//   else if !localChangedSinceSync && !remoteChangedSinceSync → noop
//   else if  localChangedSinceSync &&  remoteChangedSinceSync → CONFLICT
//   else if  remoteChangedSinceSync                          → pull
//   else if  localChangedSinceSync                           → push

import { sha256Hex } from './hash.js';
import { reconcileAttachments } from './attachment-sync.js';

export const VAULT_DIR = './Vault';
export const VAULT_PATH = `${VAULT_DIR}/vault.enc`;
export const META_PATH = `${VAULT_DIR}/vault.meta.json`;
export const ATTACHMENTS_DIR = `${VAULT_DIR}/attachments`;

export class SyncConflictError extends Error {
  constructor(local, remote) {
    super('sync conflict — local and remote both changed since last sync');
    this.name = 'SyncConflictError';
    this.local = local;
    this.remote = remote;
  }
}

export class PuterSync {
  /**
   * @param {object} opts
   * @param {*} opts.vault       LocalVault instance (already unlocked)
   * @param {*} [opts.puter]     puter SDK (defaults to globalThis.puter)
   * @param {string} [opts.vaultPath]
   * @param {string} [opts.metaPath]
   * @param {(s) => void} [opts.onStatus] receives 'idle' | 'syncing' | 'synced' | 'offline' | 'conflict' | 'error'
   */
  constructor({ vault, puter, vaultPath = VAULT_PATH, metaPath = META_PATH, attachmentsDir = ATTACHMENTS_DIR, onStatus } = {}) {
    this._vault = vault;
    this._puter = puter ?? globalThis.puter;
    this._vaultPath = vaultPath;
    this._metaPath = metaPath;
    this._attachmentsDir = attachmentsDir;
    this._onStatus = onStatus ?? (() => {});
    this._pushTimer = null;
    this._unsubscribe = null;
    this._lastError = null;
    this._inflight = null;
  }

  /** Replace the onStatus callback after construction. */
  setStatusHandler(fn) { this._onStatus = fn ?? (() => {}); }

  /** True if Puter SDK is loaded and reports a signed-in user. */
  async available() {
    try { return await this._puter?.auth?.isSignedIn?.(); }
    catch { return false; }
  }

  start({ pushDebounceMs = 5000 } = {}) {
    this.stop();
    this._unsubscribe = this._vault.onChange(() => this._scheduleDebouncedPush(pushDebounceMs));
  }

  stop() {
    if (this._unsubscribe) { this._unsubscribe(); this._unsubscribe = null; }
    if (this._pushTimer) { clearTimeout(this._pushTimer); this._pushTimer = null; }
  }

  _scheduleDebouncedPush(ms) {
    if (this._pushTimer) clearTimeout(this._pushTimer);
    this._pushTimer = setTimeout(() => {
      this._pushTimer = null;
      this.sync().catch((err) => {
        if (err instanceof SyncConflictError) return; // surfaced via status; UI takes over
        this._lastError = err;
        console.warn('sync failed', err);
      });
    }, ms);
  }

  /** Read the remote meta JSON, or null if it doesn't exist. */
  async readRemoteMeta() {
    try {
      const blob = await this._puter.fs.read(this._metaPath);
      const text = await blobToText(blob);
      const parsed = JSON.parse(text);
      if (typeof parsed?.modifiedAt !== 'number' || typeof parsed?.hash !== 'string') {
        throw new Error('remote meta has unexpected shape');
      }
      return parsed;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  }

  async readRemoteBlob() {
    const blob = await this._puter.fs.read(this._vaultPath);
    const buf = await blob.arrayBuffer();
    return new Uint8Array(buf);
  }

  /** Force a pull (replaces local vault data with remote). */
  async pull() {
    this._status('syncing');
    const bytes = await this.readRemoteBlob();
    const hash = await sha256Hex(bytes);
    await this._vault.replaceFromBlob(bytes);
    // Mark synced immediately: local data now IS the remote data, so leaving the
    // marker stale would make the next sync see both sides "changed" and raise a
    // conflict between two identical vaults. Attachment transfer is best-effort
    // and self-heals via the noop-branch backfill.
    await this._vault.setLastSynced({ at: this._vault.vaultModifiedAt(), hash });
    try {
      await reconcileAttachments({ puter: this._puter, vault: this._vault, dir: this._attachmentsDir, download: true, deleteLocal: true });
    } catch (err) {
      console.warn('attachment reconcile after pull failed; will retry next sync', err);
    }
    this._status('synced');
    return { hash };
  }

  /** Force a push (overwrites remote with local). */
  async push() {
    this._status('syncing');
    await this._ensureDir();
    // Upload attachment bytes BEFORE the vault blob, so the remote manifest
    // never references a file that isn't there yet.
    await reconcileAttachments({ puter: this._puter, vault: this._vault, dir: this._attachmentsDir, upload: true });
    // Stamp the snapshot's own modifiedAt, read in the same turn as export().
    // Reading it after the network write instead would mark any edit the user
    // made DURING the upload as already-synced: localMod > lastSyncedAt would
    // be false forever after, so that edit would never push, and the next
    // remote change would auto-pull straight over it.
    const at = this._vault.vaultModifiedAt();
    const bytes = await this._vault.export();
    const hash = await sha256Hex(bytes);
    await this._puter.fs.write(this._vaultPath, bytes);
    const meta = { modifiedAt: at, hash, size: bytes.byteLength, updatedFrom: 'web' };
    await this._puter.fs.write(this._metaPath, JSON.stringify(meta));
    // Mark synced as soon as the remote is consistent. Orphan cleanup below is
    // best-effort: letting it throw here used to skip setLastSynced entirely,
    // which manufactured a phantom conflict between two identical vaults on the
    // next sync. The noop-branch backfill retries it.
    await this._vault.setLastSynced({ at, hash });
    try {
      await reconcileAttachments({ puter: this._puter, vault: this._vault, dir: this._attachmentsDir, deleteRemote: true });
    } catch (err) {
      console.warn('orphan attachment cleanup failed; will retry next sync', err);
    }
    this._status('synced');
    return { hash };
  }

  async _ensureDir() {
    if (!this._puter?.fs?.mkdir) return;
    try {
      await this._puter.fs.mkdir(this._vaultPath.replace(/\/[^/]+$/, ''), { createMissingParents: true });
    } catch (err) {
      // If it already exists or the SDK doesn't support the option, ignore —
      // the subsequent write will fail loudly if the dir is truly missing.
      if (!isAlreadyExists(err)) {
        // Some SDK versions don't accept the option; try a plain call.
        try { await this._puter.fs.mkdir(this._vaultPath.replace(/\/[^/]+$/, '')); }
        catch (_) { /* still ignore */ }
      }
    }
  }

  /**
   * Pull a remote vault and adopt it as the local one — deriving the key
   * from the master password using the salt stored inside the remote blob.
   * This is the new-device flow: the user has unlocked with a password and
   * wants to join an existing cloud vault encrypted under that same password.
   */
  async joinFromRemote(masterPassword) {
    if (!(await this.available())) throw new Error('Puter is not available');
    this._status('syncing');
    const bytes = await this.readRemoteBlob();
    await this._vault.import(bytes, masterPassword);
    await reconcileAttachments({ puter: this._puter, vault: this._vault, dir: this._attachmentsDir, download: true });
    const hash = await sha256Hex(bytes);
    await this._vault.setLastSynced({ at: this._vault.vaultModifiedAt(), hash });
    this._status('synced');
    return { hash };
  }

  /**
   * Run the sync algorithm once. Returns one of:
   *   { action: 'noop' | 'push' | 'pull' | 'first-push' | 'first-sync-required' | 'offline' }
   * Throws SyncConflictError when both sides changed since the last sync.
   *
   * "first-sync-required" means: remote exists, local has never synced.
   * Auto-resolution would either lose local data (pull) or stomp remote
   * (push), so we hand off to the UI to ask the user explicitly.
   */
  async sync() {
    // Serialize. The debounced timer, the "Sync now" menu item and the
    // on-unlock trigger can all fire at once; two overlapping push()es
    // interleave their vault.enc / vault.meta.json writes and can leave the
    // remote with one push's blob and the other's meta, which every other
    // device then sees as a permanently-changed remote.
    if (this._inflight) return this._inflight;
    this._inflight = this._sync().finally(() => { this._inflight = null; });
    return this._inflight;
  }

  async _sync() {
    if (!(await this.available())) {
      this._status('offline');
      return { action: 'offline' };
    }
    this._status('syncing');

    const remote = await this.readRemoteMeta();
    const localMod = this._vault.vaultModifiedAt();
    const { at: lastAt, hash: lastHash } = await this._vault.getLastSynced();

    if (!remote) {
      // Only a device that has never synced may treat "no remote" as first
      // sync. If we have synced before, the remote vanishing is either a
      // transient error misread as not-found (isNotFound matches generic
      // strings like "not found") or a real deletion — pushing blindly in
      // either case can republish over another device's newer data.
      if (lastHash != null) {
        this._status('remote-missing');
        return { action: 'remote-missing' };
      }
      // Don't auto-publish an empty vault. That used to create a transient
      // empty blob in Puter — any other device joining during the window
      // between this push and the first real mutation's debounced push
      // would see an empty cloud and adopt it. Wait for the mutation
      // listener to fire a debounced push once there's actual content.
      if (this._vault.entryCount() === 0) {
        this._status('synced');
        return { action: 'first-push-skipped-empty' };
      }
      await this.push();
      return { action: 'first-push' };
    }

    if (lastHash == null) {
      this._status('first-sync-required');
      return { action: 'first-sync-required', remote };
    }

    const localChanged = localMod > lastAt;
    const remoteChanged = remote.hash !== lastHash;

    if (!localChanged && !remoteChanged) {
      // Indexes match; ensure bytes are present both ways (backfills pre-existing
      // attachments and self-heals partial failures). No deletions here.
      // Best-effort by design: the vault index already agrees on both sides, so
      // a transient attachment-listing failure must not turn an otherwise clean
      // sync into an error. The next pass retries it.
      try {
        await reconcileAttachments({ puter: this._puter, vault: this._vault, dir: this._attachmentsDir, upload: true, download: true });
      } catch (err) {
        console.warn('attachment backfill failed; will retry next sync', err);
      }
      this._status('synced');
      return { action: 'noop' };
    }
    if (localChanged && remoteChanged) {
      this._status('conflict');
      throw new SyncConflictError(
        { modifiedAt: localMod },
        { modifiedAt: remote.modifiedAt, hash: remote.hash, size: remote.size },
      );
    }
    if (remoteChanged) {
      await this.pull();
      return { action: 'pull' };
    }
    await this.push();
    return { action: 'push' };
  }

  /** "Use Local" conflict resolution. */
  async resolveUseLocal() { return this.push(); }
  /** "Use Remote" conflict resolution. */
  async resolveUseRemote() { return this.pull(); }

  _status(s) {
    try { this._onStatus(s); } catch (err) { console.warn('onStatus threw', err); }
  }
}

function isNotFound(err) {
  return errorCarries(err, [
    'subject_does_not_exist', 'enoent', 'not found', 'does not exist', 'no such file',
  ]);
}

function isAlreadyExists(err) {
  return errorCarries(err, ['already exists', 'eexist', 'already_exists']);
}

// Walks the (potentially wrapped) error object and looks for any of the
// signals in `needles` in string fields. Puter sometimes wraps the real
// reason in a batch-operation envelope; this catches the inner code without
// hard-coupling to a specific shape.
function errorCarries(err, needles) {
  const seen = new Set();
  const fields = [];
  const visit = (v) => {
    if (v == null || seen.has(v)) return;
    if (typeof v === 'string' || typeof v === 'number') { fields.push(String(v)); return; }
    if (typeof v !== 'object') return;
    seen.add(v);
    for (const k of ['code', 'status', 'message', 'error', 'reason', 'cause', 'description', 'detail', 'errors']) {
      if (k in v) visit(v[k]);
    }
    if (Array.isArray(v)) for (const item of v) visit(item);
  };
  visit(err);
  const hay = fields.join(' ').toLowerCase();
  return needles.some((n) => hay.includes(n));
}

async function blobToText(blob) {
  if (typeof blob?.text === 'function') return blob.text();
  if (typeof blob === 'string') return blob;
  if (blob instanceof Uint8Array) return new TextDecoder().decode(blob);
  if (blob instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(blob));
  throw new Error('blobToText: unsupported response type');
}
